/**
 * Items v2 set bonuses (docs/systems/ITEMS_V2.md §1.6, R5): a set is pieces from one source; wearing 2 / 4 of them pays a
 * fixed bonus on top of each piece's own random affixes. A bonus is stats (summed in `equipmentStats`) and/or an
 * effect the combat code plays (systems/gearCombat.ts reads `EquipmentStats.sets`). Pieces carry `set` in their
 * ItemDef (data/bossGear.ts). Data only.
 */
import type { ItemStats } from './items';

/** Effects a set may play (systems/gearCombat.ts). */
export type SetEffect =
  /** A crit of the wearer's blow sets the foe burning (the พิษ-type damage over time) for `burnSec`. */
  | 'critBurn'
  /** Each blow the wearer blocks charges; at `charges` a ring of มึนงง goes off round them. */
  | 'blockCharge'
  /** None: the stats are the bonus. */
  | 'none';

export interface SetBonus {
  /** Pieces worn for it. */
  readonly n: number;
  readonly stats?: Readonly<ItemStats>;
  readonly effect?: SetEffect;
  /** Thai line for the tooltip. */
  readonly text: string;
}

export interface SetDef {
  readonly id: string;
  readonly name: string;
  readonly pieces: readonly string[];
  readonly bonuses: readonly SetBonus[];
}

/** The numbers of the set effects. */
export const SET_FX = {
  /** critBurn: seconds of burning (as the poison status, one stack). */
  burnStacks: 1,
  /** blockCharge: charges for a ring, its reach (m) and the stun (s). */
  charges: 3,
  ringReach: 2.5,
  ringStun: 0.6,
} as const;

const list: SetDef[] = [
  {
    id: 'wb-ape',
    name: 'ชุดวานรทงปี้',
    pieces: ['wb-bell-sash', 'wb-ape-band', 'wb-ape-greaves', 'wb-ape-boots'],
    bonuses: [
      { n: 2, stats: { flyCostPct: 0.1 }, text: 'ลดพลังกายตอนเหาะ 10%' },
      { n: 4, stats: { airDmg: 0.15 }, text: 'โจมตีกลางอากาศแรงขึ้น 15%' },
    ],
  },
  {
    id: 'wb-firebird',
    name: 'ชุดขนนกเพลิง',
    pieces: ['wb-firebird-cape', 'wb-firebird-crown', 'wb-firebird-greaves', 'wb-firebird-boots'],
    bonuses: [
      { n: 2, stats: { resFire: 0.15 }, text: 'ต้านธาตุไฟ 15%' },
      { n: 4, effect: 'critBurn', text: 'ติดคริแล้วศัตรูติดไฟ (พิษ 1 ชั้น)' },
    ],
  },
  {
    id: 'wb-thunder-turtle',
    name: 'ชุดกระดองอสนี',
    pieces: ['wb-shell-pauldron', 'wb-shell-helm', 'wb-shell-greaves', 'wb-shell-boots'],
    bonuses: [
      { n: 2, stats: { guardHp: 0.1 }, text: 'ความทนการ์ด 10%' },
      { n: 4, effect: 'blockCharge', text: 'ตั้งรับสำเร็จสะสมประจุ ครบ 3 ปล่อยมึนงง 0.6 วินาทีรอบตัว' },
    ],
  },
  {
    // ถ้ำคลื่นคำราม's chest (data/dungeon.ts DUNGEON_GEAR); its pieces need no `set` field: SET_OF_PIECE maps them.
    id: 'dg-wave',
    name: 'ชุดคลื่นคำราม',
    pieces: ['dg-set-head', 'dg-set-body', 'dg-set-legs', 'dg-set-feet'],
    bonuses: [
      { n: 2, stats: { resWater: 0.1 }, text: 'ต้านธาตุน้ำ 10%' },
      { n: 4, stats: { maxStamina: 30, hpRegen: 1 }, text: 'พลังกายสูงสุด +30 · ฟื้นพลังชีวิต +1' },
    ],
  },
];

export const ITEM_SETS: Readonly<Record<string, SetDef>> = Object.fromEntries(list.map((s) => [s.id, s]));
export const SET_LIST: readonly SetDef[] = list;
/** Which set each piece (base item id) belongs to. */
export const SET_OF_PIECE: Readonly<Record<string, string>> = Object.fromEntries(
  list.flatMap((s) => s.pieces.map((p) => [p, s.id])),
);

/** The bonuses a set pays at `worn` pieces. */
export const setBonusesAt = (set: SetDef, worn: number): readonly SetBonus[] => set.bonuses.filter((b) => worn >= b.n);
